import { alpha, createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark' | 'system';
export type ResolvedMode = 'light' | 'dark';

const SANS = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

const light = {
  primary: { main: '#17695f', light: '#288a7c', dark: '#0e544c', contrastText: '#ffffff' },
  secondary: { main: '#526c64', light: '#709186', dark: '#3f5850', contrastText: '#ffffff' },
  success: { main: '#167b57', light: '#2a9d72', dark: '#0f6547' },
  warning: { main: '#b45309', light: '#f59e0b', dark: '#92400e' },
  error: { main: '#dc2626', light: '#ef4444', dark: '#b91c1c' },
  info: { main: '#28786e', light: '#499489', dark: '#1e6259' },
  background: { default: '#f6f5f0', paper: '#fffefa' },
  text: { primary: '#1d2928', secondary: '#60706b', disabled: '#95a29d' },
  divider: '#d9dfda',
};

const dark = {
  primary: { main: '#85d4b8', light: '#a8e8d1', dark: '#56b89b', contrastText: '#10231e' },
  secondary: { main: '#8ebdb0', light: '#b4d9cd', dark: '#6e9d90', contrastText: '#10231e' },
  success: { main: '#4ade80', light: '#86efac', dark: '#22c55e' },
  warning: { main: '#fbbf24', light: '#fcd34d', dark: '#f59e0b' },
  error: { main: '#f87171', light: '#fca5a5', dark: '#ef4444' },
  info: { main: '#8dcfc0', light: '#b3e6d6', dark: '#67b9a8' },
  background: { default: '#111a1a', paper: '#1b2928' },
  text: { primary: '#e8eee9', secondary: '#a5b8af', disabled: '#768d82' },
  divider: '#354640',
};

/** Semantic colours the Markdown surface needs but MUI's palette has no slot for. */
export interface MarkdownPalette {
  surfaceRaised: string;
  surfaceSunken: string;
  codeBackground: string;
  codeBorder: string;
  inlineCodeBackground: string;
  inlineCodeText: string;
  tableHeader: string;
  tableStripe: string;
  quoteBar: string;
  searchHit: string;
  searchHitActive: string;
  glass: string;
}

const markdownPalette = (mode: ResolvedMode): MarkdownPalette =>
  mode === 'light'
    ? {
        surfaceRaised: '#fffefa',
        surfaceSunken: '#f1f3ed',
        codeBackground: '#f7f8f4',
        codeBorder: '#d9dfda',
        inlineCodeBackground: alpha('#17695f', 0.09),
        inlineCodeText: '#0e544c',
        tableHeader: '#f1f3ed',
        tableStripe: alpha('#17695f', 0.025),
        quoteBar: '#8ab3a5',
        searchHit: alpha('#f59e0b', 0.35),
        searchHitActive: alpha('#f59e0b', 0.85),
        glass: '#fffefa',
      }
    : {
        surfaceRaised: '#21312f',
        surfaceSunken: '#14201e',
        codeBackground: '#15211f',
        codeBorder: '#354640',
        inlineCodeBackground: alpha('#85d4b8', 0.14),
        inlineCodeText: '#a8e8d1',
        tableHeader: '#23332f',
        tableStripe: alpha('#85d4b8', 0.035),
        quoteBar: '#587e70',
        searchHit: alpha('#fbbf24', 0.3),
        searchHitActive: alpha('#fbbf24', 0.75),
        glass: '#1b2928',
      };

declare module '@mui/material/styles' {
  interface Theme {
    markdown: MarkdownPalette;
  }
  interface ThemeOptions {
    markdown?: MarkdownPalette;
  }
}

const baseOptions = (mode: ResolvedMode): ThemeOptions => ({
  palette: { mode, ...(mode === 'light' ? light : dark) },
  markdown: markdownPalette(mode),
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: SANS,
    fontSize: 15,
    h1: { fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1.12 },
    h2: { fontWeight: 650, letterSpacing: '-0.025em', lineHeight: 1.2 },
    h3: { fontWeight: 650, letterSpacing: '-0.016em', lineHeight: 1.3 },
    h4: { fontWeight: 650, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    body1: { lineHeight: 1.72 },
    body2: { lineHeight: 1.65 },
  },
});

const componentOverrides = (theme: Theme): ThemeOptions => ({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': { colorScheme: theme.palette.mode },
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
        body: { backgroundColor: theme.palette.background.default },
        'code, kbd, samp, pre': { fontFamily: MONO },
        '*::selection': { background: alpha(theme.palette.primary.main, 0.24) },
        '*': { scrollbarColor: `${theme.palette.divider} transparent` },
        '*::-webkit-scrollbar': { width: 11, height: 11 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.text.primary, 0.18),
          borderRadius: 999,
          border: '3px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: alpha(theme.palette.text.primary, 0.32),
          backgroundClip: 'content-box',
          border: '3px solid transparent',
        },
        // Give in-page anchors room to clear the sticky app bar.
        '[id]': { scrollMarginTop: '84px' },
        ':focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
          borderRadius: 4,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: {
          backgroundColor: theme.markdown.glass,
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: theme.palette.divider },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: 150,
          }),
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 400 },
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.mode === 'light' ? '#1d2928' : '#354640',
          fontSize: 12,
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 10px',
        },
        arrow: { color: theme.palette.mode === 'light' ? '#1d2928' : '#354640' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, letterSpacing: 0.1 },
        sizeSmall: { height: 22, fontSize: 11 },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none', borderColor: theme.palette.divider },
      },
    },
    MuiAlert: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: { root: { borderRadius: 12, alignItems: 'flex-start' } },
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 999 } },
    },
  },
});

/** Builds the fully composed MUI theme for a resolved colour scheme. */
export const createAppTheme = (mode: ResolvedMode): Theme => {
  const base = createTheme(baseOptions(mode));
  return createTheme(base, componentOverrides(base));
};
